import { applyActionCode, confirmPasswordReset, verifyPasswordResetCode } from "firebase/auth";
import { CircleCheck, CircleX } from "lucide-react";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router";
import { useAuth } from "../../auth/AuthProvider";
import { Button, ButtonLink } from "../../components/Button";
import { FormAlert, SecretField, focusFirstInvalid } from "../../components/fields";
import { useDocumentTitle } from "../../components/layout";
import { Spinner } from "../../components/Spinner";
import { authErrorMessage } from "../../lib/firebase";
import { takeNext } from "../guards";
import { AuthLayout } from "./AuthLayout";

type State =
  | { kind: "working" }
  | { kind: "verified" }
  | { kind: "reset-form"; email: string }
  | { kind: "reset-done"; email: string }
  | { kind: "error"; message: string; retry: "verify" | "reset" | null };

/**
 * In-app handler for Firebase email action links:
 *   /auth/action?mode=verifyEmail|resetPassword&oobCode=…
 */
export function AuthActionPage() {
  const { auth, user, refreshVerification } = useAuth();
  const [params] = useSearchParams();
  const mode = params.get("mode");
  const code = params.get("oobCode");
  const [state, setState] = useState<State>({ kind: "working" });
  const started = useRef(false);
  useDocumentTitle(mode === "resetPassword" ? "Choose a new password" : "Verify email");

  useEffect(() => {
    if (started.current) return; // action codes are single-use; never apply twice (StrictMode)
    started.current = true;
    if (!code || (mode !== "verifyEmail" && mode !== "resetPassword")) {
      setState({ kind: "error", message: "This link is incomplete or not one Supperkeep can handle. Request a new one.", retry: null });
      return;
    }
    if (mode === "verifyEmail") {
      applyActionCode(auth, code)
        .then(async () => {
          if (auth.currentUser) {
            try {
              await refreshVerification();
            } catch {
              // verification is applied; the next sign-in will pick it up
            }
          }
          setState({ kind: "verified" });
        })
        .catch((error) => setState({ kind: "error", message: authErrorMessage(error), retry: "verify" }));
    } else {
      verifyPasswordResetCode(auth, code)
        .then((email) => setState({ kind: "reset-form", email }))
        .catch((error) => setState({ kind: "error", message: authErrorMessage(error), retry: "reset" }));
    }
  }, [auth, code, mode, refreshVerification]);

  if (state.kind === "working") {
    return (
      <AuthLayout title={mode === "resetPassword" ? "Checking your link" : "Verifying your email"}>
        <div className="auth-sent">
          <Spinner size="lg" label="Checking link" />
        </div>
      </AuthLayout>
    );
  }

  if (state.kind === "verified") {
    return (
      <AuthLayout title="Email verified">
        <div className="auth-sent auth-sent--success">
          <CircleCheck size={44} aria-hidden="true" />
          <p>Thanks — your email is confirmed.</p>
        </div>
        {user ? (
          <ButtonLink to={takeNext()} emphasis="solid" intent="brand" size="lg" block replace>
            Continue to Supperkeep
          </ButtonLink>
        ) : (
          <ButtonLink to="/login" emphasis="solid" intent="brand" size="lg" block>
            Sign in
          </ButtonLink>
        )}
      </AuthLayout>
    );
  }

  if (state.kind === "error") {
    return (
      <AuthLayout title="This link didn't work" footer={<Link to="/login">Back to sign in</Link>}>
        <div className="auth-sent auth-sent--error">
          <CircleX size={44} aria-hidden="true" />
          <p>{state.message}</p>
        </div>
        {state.retry === "reset" ? (
          <ButtonLink to="/forgot-password" emphasis="solid" intent="brand" block>
            Request a new reset link
          </ButtonLink>
        ) : state.retry === "verify" ? (
          <ButtonLink to={user ? "/verify-email" : "/login"} emphasis="solid" intent="brand" block>
            {user ? "Send a new verification email" : "Sign in to get a new link"}
          </ButtonLink>
        ) : null}
      </AuthLayout>
    );
  }

  if (state.kind === "reset-done") {
    return (
      <AuthLayout title="Password changed">
        <div className="auth-sent auth-sent--success">
          <CircleCheck size={44} aria-hidden="true" />
          <p>
            You can now sign in as <strong>{state.email}</strong> with your new password.
          </p>
        </div>
        <ButtonLink to={`/login?reset=1&email=${encodeURIComponent(state.email)}`} emphasis="solid" intent="brand" size="lg" block>
          Sign in
        </ButtonLink>
      </AuthLayout>
    );
  }

  return <ResetForm email={state.email} code={code!} onDone={() => setState({ kind: "reset-done", email: state.email })} onExpired={(message) => setState({ kind: "error", message, retry: "reset" })} />;
}

function ResetForm({ email, code, onDone, onExpired }: { email: string; code: string; onDone: () => void; onExpired: (message: string) => void }) {
  const { auth } = useAuth();
  const formRef = useRef<HTMLFormElement>(null);
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | undefined>();
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (busy) return;
    if (password.length < 8) {
      setError("Use at least 8 characters.");
      focusFirstInvalid(formRef.current);
      return;
    }
    setError(undefined);
    setFormError(null);
    setBusy(true);
    try {
      await confirmPasswordReset(auth, code, password);
      onDone();
    } catch (err) {
      const errCode = typeof err === "object" && err && "code" in err ? String((err as { code: string }).code) : "";
      if (errCode === "auth/expired-action-code" || errCode === "auth/invalid-action-code") onExpired(authErrorMessage(err));
      else setFormError(authErrorMessage(err));
      setBusy(false);
    }
  };

  return (
    <AuthLayout title="Choose a new password" lead={<>For <strong>{email}</strong></>}>
      <form ref={formRef} noValidate onSubmit={submit} className="stack">
        <FormAlert>{formError}</FormAlert>
        <input type="email" name="username" autoComplete="username" value={email} readOnly hidden />
        <SecretField label="New password" autoComplete="new-password" value={password} error={error} hint="At least 8 characters." onChange={(e) => setPassword(e.target.value)} minLength={8} required />
        <Button type="submit" emphasis="solid" intent="brand" size="lg" block busy={busy}>
          Save new password
        </Button>
      </form>
    </AuthLayout>
  );
}
